import React from 'react';

import { User } from '@repo/domain/user';
import { Button } from '@repo/ui/components/Buttons';
import CreditsIcon from '@repo/ui/components/Icon/CreditsIcon';
import { themeStyle } from '@repo/ui/theming';
import { addSeconds } from 'date-fns';

import {
  promotionSelector,
  usePromotionStore,
} from '../../stores/promotionStore';
import { useUserStore, userStoreSelector } from '../../stores/userStore';
import { UserCreditsInfo, getUserCreditsInfo } from '../../utils/credits';
import { ImageGenerationModel } from '../AiGenerators/types';
import { getUpgradeModalAiPanel } from '../AiGenerators/utils';

import {
  BannerContainer,
  CreditLimitText,
  LimitTextContainer,
  TextContainer,
} from './styles';

const buttomTheme = {
  padding: '14px',
  fontSize: themeStyle.fontSize12,
};

export enum CreditLimitBannerDisplayIn {
  USER_NAVIGATION_POPUP = 'USER_NAVIGATION_POPUP',
  AI_MENU = 'AI_MENU',
  SVG_CONVERTER_WEBSITE = 'SVG_CONVERTER_WEBSITE',
  BACKGROUND_REPLACER_WEBSITE = 'BACKGROUND_REPLACER_WEBSITE',
  IMAGE_GENERATION_MICRO_APP = 'IMAGE_GENERATION_MICRO_APP',
}

const isDisplayedInWebsite = (displayIn: CreditLimitBannerDisplayIn): boolean =>
  [
    CreditLimitBannerDisplayIn.BACKGROUND_REPLACER_WEBSITE,
    CreditLimitBannerDisplayIn.SVG_CONVERTER_WEBSITE,
    CreditLimitBannerDisplayIn.IMAGE_GENERATION_MICRO_APP,
  ].includes(displayIn);

const getExpirationDateLabel = (timestamp: number): string => {
  const expiresAtDate = new Date(timestamp);
  const newCreditsDate = addSeconds(expiresAtDate, 1);
  return `${newCreditsDate.toLocaleDateString()} - ${newCreditsDate.toLocaleTimeString()}`;
};

interface CreditsLimitData {
  title: React.ReactElement | string;
  description?: React.ReactElement | string;
  showUpgradeButton?: boolean;
  gap?: string;
}

const getCreditsLimitData = (
  displayIn: CreditLimitBannerDisplayIn,
  user: User | null,
  userCreditsInfo: UserCreditsInfo
): CreditsLimitData | null => {
  if (!user) {
    return null;
  }

  const { extraCredits, resetTime, remainingAmount, amount } = userCreditsInfo;

  const creditsResetIn = (
    <>
      Credits reset in&nbsp;
      <b>{getExpirationDateLabel(resetTime)}</b>
    </>
  );

  // For the FREE plan we always show remaining extra credits and upgrade button in the user navigation popup.
  // In the sidebar only if 0 left.
  if (!user.plan) {
    if (displayIn === CreditLimitBannerDisplayIn.AI_MENU && extraCredits > 0) {
      return null;
    }
    return {
      title: (
        <>
          {extraCredits}&nbsp;<b>Credits</b>
        </>
      ),
      description: isDisplayedInWebsite(displayIn)
        ? `Upgrade your plan with up to unlimited credits.`
        : undefined,
      showUpgradeButton: true,
      // Need small gap on the website for mobile version
      gap: isDisplayedInWebsite(displayIn) ? '8px' : '0px',
    };
  } else {
    // For the PRO and EXPERT plans:
    // - If the user has run out of monthly credits, we show the remaining extra credits or the reset time.
    //   Together with the upgrade button.
    // - If the user still has monthly credits, we show the remaining amount and the reset time (only for the profile popup).
    //   Together with the upgrade button if the remaining amount is 5 or below.
    if (
      remainingAmount > 0 &&
      displayIn === CreditLimitBannerDisplayIn.USER_NAVIGATION_POPUP
    ) {
      return {
        title: (
          <>
            {`${remainingAmount}/${amount}`}&nbsp;
            <b>Monthly Credits</b>
          </>
        ),
        description: creditsResetIn,
        showUpgradeButton: remainingAmount <= 5,
      };
    }

    if (remainingAmount === 0) {
      return {
        title: <b>Monthly credit limit reached</b>,
        description: extraCredits ? (
          <>
            You are using your extra credits:&nbsp;
            <b>{extraCredits}&nbsp;Credits</b>
          </>
        ) : (
          creditsResetIn
        ),
        showUpgradeButton: true,
      };
    }
  }

  return null;
};

/**
 * @deprecated This component is deprecated and will be removed soon.
 */
const CreditsLimitBanner: React.FC<{
  displayIn: CreditLimitBannerDisplayIn;
  className?: string;
  generatingModelForTracking?: ImageGenerationModel;
}> = ({ generatingModelForTracking, className, displayIn }) => {
  const showUpgradeModal = usePromotionStore(
    promotionSelector.showUpgradeModal
  );

  const user = useUserStore(userStoreSelector.user);

  if (!user) {
    return null;
  }

  const userCreditsInfo = getUserCreditsInfo(user);
  const { total } = userCreditsInfo;

  const creditsLimitData = getCreditsLimitData(
    displayIn,
    user,
    userCreditsInfo
  );

  if (!creditsLimitData) {
    return null;
  }

  const onUpgradePlanButtonClick = (): void => {
    let upgradeModalTrackingLabel = 'upgradePlanWithMoreCreditsAIPanel';
    if (displayIn === CreditLimitBannerDisplayIn.USER_NAVIGATION_POPUP) {
      upgradeModalTrackingLabel =
        'upgradePlanWithMoreCreditsUserNavigationPopup';
    } else if (displayIn === CreditLimitBannerDisplayIn.SVG_CONVERTER_WEBSITE) {
      upgradeModalTrackingLabel = 'upgradePlanWithMoreCreditsSvgConverter';
    } else if (
      displayIn === CreditLimitBannerDisplayIn.BACKGROUND_REPLACER_WEBSITE
    ) {
      upgradeModalTrackingLabel =
        'upgradePlanWithMoreCreditsBackgroundReplacer';
    } else if (
      displayIn === CreditLimitBannerDisplayIn.IMAGE_GENERATION_MICRO_APP
    ) {
      upgradeModalTrackingLabel =
        'upgradePlanWithMoreCreditsImageGenerationMicroApp';
    }
    const modalToShow = getUpgradeModalAiPanel(user?.plan, total);

    if (modalToShow) {
      showUpgradeModal(modalToShow, {
        textToImageModelVersion: generatingModelForTracking,
        trackingLabel: upgradeModalTrackingLabel,
      });
    }
  };

  return (
    <BannerContainer className={className} gap={creditsLimitData.gap || '24px'}>
      <TextContainer>
        <LimitTextContainer>
          <CreditsIcon height="20px" />
          <CreditLimitText data-testid="credits-banner-title">
            {creditsLimitData.title}
          </CreditLimitText>
        </LimitTextContainer>
        {creditsLimitData.description && (
          <CreditLimitText data-testid="credits-banner-description">
            {creditsLimitData.description}
          </CreditLimitText>
        )}
      </TextContainer>
      {creditsLimitData.showUpgradeButton && user.plan !== 'EXPERT' && (
        <Button
          dataTestId="credits-banner-button"
          height="30px"
          label="Upgrade Plan"
          name="upgradePlanButton"
          theme={buttomTheme}
          width="110px"
          onClick={onUpgradePlanButtonClick}
        />
      )}
    </BannerContainer>
  );
};

export default CreditsLimitBanner;
